import { FrequencyType } from 'components/NewsFeed/PollTaskForm/utils'
import { transformSubtasks } from 'components/TasksV2/TaskCreation/utils'
import { sortContentAndSubtasks } from 'components/TasksV2/TasksOverview/utils'
import { Subtask, Task } from 'constants/interfaces'
import keys from 'constants/keys'
import dayjs from 'dayjs'
import { isNumber } from 'lodash'
import { RecurrenceTsTaskInsights, TaskFromInsights, TaskInsightsResult, TasksInsights } from 'redux/actions/tasks_v2'
import { getDayIndicesInRange } from 'utils'
import { CHART_COLORS } from 'utils/taskAnalytics'

const DEFAULT_OVERDUE_DAYS = 7
const DEFAULT_OVERDUE_DAYS_RECURRING = 0

type CalculateTaskCompletionArgs = {
  is_individual_results: boolean
  results: TaskInsightsResult[]
  recurrenceTsArray?: RecurrenceTsTaskInsights[]
  // Only the keys (subtask count) are used, so accept either shape the server returns.
  subTasks: {
    [key: string]: unknown
  }
  groupsAudience: string[]
  usersAudience: string[]
}

type StatusCounts = {
  done: number
  'in progress': number
  todo: number
  approved: number // NEW: Track approved status
  rejected: number // NEW: Track rejected/disapproved status
}

export type AnalyticalItem = {
  name: string
  value: number
  color: string
}

export type Analytics = AnalyticalItem[]

export type RecurrenceAnalytics = Record<string, AnalyticalItem[]>

export type CalculateTaskCompletionReturnType = Analytics | RecurrenceAnalytics

const calculateStatuses = (
  audiences: string[],
  contextResults: Record<string, Record<string, number>>,
  totalSubtasks: number
): StatusCounts => {
  const statuses = audiences.map((context) => {
    const subtasks = contextResults[context] || {}

    const statusCounts = Object.values(subtasks).reduce(
      (acc, status) => {
        if (status === keys.TASK_STATUSES.DONE.value || status === keys.TASK_STATUSES.SKIPPED.value) {
          acc.done++
        } else if (status === keys.TASK_STATUSES.INPROGRESS.value) {
          acc['in progress']++
        } else if (status === keys.TASK_STATUSES.TODO.value) {
          acc.todo++
        } else if (status === keys.TASK_STATUSES.APPROVED.value) {
          // NEW: Track approved
          acc.approved++
        } else if (status === keys.TASK_STATUSES.DISAPPROVED.value) {
          // NEW: Track rejected/disapproved
          acc.rejected++
        }
        return acc
      },
      {
        todo: 0,
        'in progress': 0,
        done: 0,
        approved: 0,
        rejected: 0,
      }
    )

    // NEW: Check for all approved or any rejected
    const allApproved = statusCounts.approved === totalSubtasks
    const hasRejected = statusCounts.rejected > 0

    if (allApproved) {
      return 'approved'
    }
    if (hasRejected) {
      return 'rejected'
    }
    if (statusCounts.done === totalSubtasks) {
      return 'done'
    }
    if (statusCounts.done + statusCounts['in progress'] + statusCounts.approved > 0) {
      return 'in progress'
    }
    return 'todo'
  })

  return statuses.reduce(
    (acc, status) => {
      acc[status]++
      return acc
    },
    {
      done: 0,
      'in progress': 0,
      todo: 0,
      approved: 0,
      rejected: 0,
    }
  )
}

const calculateAnalytics = (statusCounts: StatusCounts, totalContexts: number) => {
  const doneValue = (statusCounts.done / totalContexts) * 100
  const inProgressValue = (statusCounts['in progress'] / totalContexts) * 100
  const todoValue = (statusCounts.todo / totalContexts) * 100
  const approvedValue = (statusCounts.approved / totalContexts) * 100 // NEW
  const rejectedValue = (statusCounts.rejected / totalContexts) * 100 // NEW

  return [
    {
      name: 'task_status_todo',
      value: Math.round(todoValue),
      color: CHART_COLORS.todo,
      textColor: CHART_COLORS.todoText, // pastel grey to match status blocks
    },
    {
      name: 'task_status_inprogress',
      value: Math.round(inProgressValue),
      color: CHART_COLORS.inProgress,
      textColor: CHART_COLORS.inProgressText, // pastel yellow to match status blocks
    },
    {
      name: 'task_status_done',
      value: Math.round(doneValue),
      color: CHART_COLORS.done,
      textColor: CHART_COLORS.doneText, // pastel blue to match status blocks
    },
    {
      name: 'task_status_approved',
      value: Math.round(approvedValue),
      color: CHART_COLORS.approved,
      textColor: CHART_COLORS.approvedText, // pastel green to match status blocks
    },
    {
      name: 'task_status_rejected',
      value: Math.round(rejectedValue),
      color: CHART_COLORS.rejected,
      textColor: CHART_COLORS.rejectedText, // pastel red to match status blocks
    },
  ]
}

const groupResultsByContext = (
  results: TaskInsightsResult[],
  useRecurrenceTs: boolean
): Record<string, Record<string, number>> | Record<string, Record<string, Record<string, number>>> => {
  if (!useRecurrenceTs) {
    const groupedByContext: Record<string, Record<string, number>> = {}

    for (const { context_id, st_id, status } of results) {
      if (!context_id) continue

      if (!groupedByContext[context_id]) {
        groupedByContext[context_id] = {}
      }

      groupedByContext[context_id][st_id] = status
    }

    return groupedByContext
  }

  const groupedByRecurrence: Record<string, Record<string, Record<string, number>>> = {}

  for (const { recurrence_ts, context_id, st_id, status } of results) {
    if (!recurrence_ts) continue

    if (!groupedByRecurrence[recurrence_ts]) {
      groupedByRecurrence[recurrence_ts] = {}
    }

    if (!groupedByRecurrence[recurrence_ts][context_id]) {
      groupedByRecurrence[recurrence_ts][context_id] = {}
    }

    groupedByRecurrence[recurrence_ts][context_id][st_id] = status
  }

  return groupedByRecurrence
}

export const calculateTaskCompletion = ({
  is_individual_results,
  results,
  subTasks,
  groupsAudience,
  usersAudience,
  recurrenceTsArray,
}: CalculateTaskCompletionArgs): CalculateTaskCompletionReturnType => {
  const audiences = is_individual_results ? usersAudience : [...groupsAudience, ...usersAudience]
  const totalSubtasks = Object.keys(subTasks).length

  if (!recurrenceTsArray) {
    const contextResults = groupResultsByContext(results, false) as Record<string, Record<string, number>>
    const statusCounts = calculateStatuses(audiences, contextResults, totalSubtasks)
    return calculateAnalytics(statusCounts, audiences.length)
  }

  const resultsByRecurrence = groupResultsByContext(results, true) as Record<
    string,
    Record<string, Record<string, number>>
  >

  return recurrenceTsArray.reduce((acc, { recurrence_ts }) => {
    const contextResults = resultsByRecurrence[recurrence_ts] || {}
    const statusCounts = calculateStatuses(audiences, contextResults, totalSubtasks)

    acc[recurrence_ts] = calculateAnalytics(statusCounts, audiences.length)
    return acc
  }, {} as Record<string, AnalyticalItem[]>)
}

type PrepareDataForTaskFromArgs = {
  task: Task
  taskFromInsights: TaskFromInsights
  subtasks: Subtask[]
  isPassAdditionalData: boolean
}

export const prepareDataForTaskFrom = ({
  task,
  taskFromInsights,
  subtasks,
  isPassAdditionalData,
}: PrepareDataForTaskFromArgs) => {
  const {
    title,
    desc,
    attached_files,
    attached_videos,
    attached_images,
    groups,
    users,
    tags,
    is_individual_results,
    allowed_overdue_days,
    view,
    contents,
    priority,
    watchers,
    auditors,
    type,
  } = task
  const { from_ts, deadline, review_to_ts, is_recurring, recurrence_ts, recurrence_label, recurrence_days_of_week } =
    taskFromInsights

  const contentsWithType =
    contents?.map((content) => ({
      ...content,
      type: keys.SUBTASK_TYPES.DESCRIPTION,
    })) ?? []
  const orderedSubtasks = sortContentAndSubtasks(subtasks, contentsWithType)

  const transformedSubtasks = transformSubtasks(orderedSubtasks)

  const formattedFromTs = dayjs(from_ts, 'YYYYMMDDHHmmss').format('YYYY-MM-DDTHH:mm:ss.SSS[Z]')
  const formattedDeadline = dayjs(deadline, 'YYYYMMDDHHmmss').format('YYYY-MM-DDTHH:mm:ss.SSS[Z]')
  const formattedReviewToTs = review_to_ts
    ? dayjs(review_to_ts, 'YYYYMMDDHHmmss').format('YYYY-MM-DDTHH:mm:ss.SSS[Z]')
    : null
  const formattedFrequencyTime = is_recurring ? dayjs(formattedDeadline).format('HH:mm') : ''
  const recurrenceTs = is_recurring ? recurrence_ts[0] : null

  const recurringRange =
    recurrenceTs && isNumber(recurrence_label)
      ? getDayIndicesInRange({
          recurrenseStartTs: recurrenceTs.recurrence_ts,
          recurrenseDeadlineTs: recurrenceTs.recurrence_deadline,
          frequencyType: recurrence_label,
          recurrenceDaysOfWeek: recurrence_days_of_week,
        })
      : null

  const baseData = {
    SUBTASK_TASK_CREATION: {
      title,
      description: {
        desc,
        attached_files,
        attached_images,
        attached_videos,
      },
      sub_tasks: transformedSubtasks,
    },
  }

  const calculatedOverdueDays = !isNumber(allowed_overdue_days)
    ? is_recurring
      ? DEFAULT_OVERDUE_DAYS_RECURRING
      : DEFAULT_OVERDUE_DAYS
    : allowed_overdue_days

  // Extract disable_gallery_upload from the first Image or Video subtask.
  // All media subtasks share the same value so reading one is sufficient.
  const firstMediaSubtask = subtasks.find(
    (st) => st.type === keys.SUBTASK_TYPES.IMAGE_SUBTASK || st.type === keys.SUBTASK_TYPES.VIDEO_SUBTASKS
  )
  const disable_gallery_upload = firstMediaSubtask?.disable_gallery_upload ?? false

  const additionalData = isPassAdditionalData
    ? {
        TASK_SETTINGS: {
          audience: {
            groups,
            users,
            tags,
          },
          from_ts: formattedFromTs,
          deadline: formattedDeadline,
          view,
          is_individual_results,
          allowed_overdue_days: calculatedOverdueDays,
          priority,
          watchers,
          type,
          auditors,
          disable_gallery_upload,
        },
        TIME_FRAME_SELECTION: {
          from_ts: formattedFromTs,
          deadline: formattedDeadline,
          is_recurring,
          days_of_week:
            recurrence_label === FrequencyType.WEEK || recurrence_label === FrequencyType.DAY ? recurringRange : null,
          monthly_range: recurrence_label === FrequencyType.MONTH ? recurringRange : null,
          frequency_time: formattedFrequencyTime,
          recurrence_frequency: recurrence_label,
          allowed_overdue_days: calculatedOverdueDays,
          review_to_ts: formattedReviewToTs,
        },
      }
    : {}

  const data = { ...baseData, ...(isPassAdditionalData ? additionalData : {}) }

  return data
}

export const formatDate = (timestamp: string) => dayjs(timestamp).format('DD/MM/YY HH:mm')

export const removeKeyFromInsights = (insights: TasksInsights, key: string): TasksInsights =>
  Object.keys(insights).reduce((acc, curr) => (curr !== key ? { ...acc, [curr]: insights[curr] } : acc), {})

export const getFrequency = (recurrence_label: number) => {
  switch (recurrence_label) {
    case FrequencyType.DAY:
      return 't_daily'
    case FrequencyType.WEEK:
      return 't_weekly'
    case FrequencyType.MONTH:
      return 't_monthly'
    default:
      return ''
  }
}
